import { useState, useMemo, useRef, useEffect, useReducer } from 'react';
import { EditorLinks } from './EditorLinks';
import { EditorChildren } from './EditorChildren';
import './PropertyGrid.less';
import {
  getChildrenFromSnapshot,
  IPropertyChild,
  compareEditorChildren,
  findContextChildByPath,
  providerNodeSearch,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { SearchField } from 'components/inputs/SearchField';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { PropertyGridButtons, IPropertyGridButtonsProps } from './PropertyGridButtons';
import {
  getAllValidationIssues,
  NodeValidationIssues,
  getValidationSummary,
  REF_PROP_SEPARATOR,
} from 'utils/ValidationUtils';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContext';
import { getInvalidReasons } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { getContextChildrenTotal, shouldHideTopCategory } from './utils/PropertyGridUtils';
import { contextProviderNodeReducer } from 'components/property-editors/contextProviderNodeReducer';
import { IReportsChildCountProps } from './EditorCategoryContext';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { ValidationError } from 'components/inputs/Validation/ValidationError';
import {
  defaultPropertyEditorContext,
  propertyEditorContextHelper,
  PropertyEditorContextProvider,
  QuickChangeActionHandler,
  ShowDialogueHandler,
} from 'components/property-editors/contexts/PropertyEditorContext';
import { LegacyEditorOptions } from 'components/Editors/legacy-editors/LegacyEditor';
import classNames from 'classnames';
import { ActionDisplayType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { IAwaitableContext } from 'contexts/AwaitableContext';
import { IAddBeforeSavePromises } from './hooks/useEditComplete';
import { DebounceComponent } from 'components/DebounceComponent';
import { Spinner } from 'components/Spinner';
import { debounce } from 'debounce';
import { useSubscribeToEditingContext } from 'components/property-editors/hooks/useSubscribeToEditingContext';
import { useUpdatesPropertyEditors } from 'components/property-editors/hooks/useUpdatesPropertyEditors';
import { getEditingContext } from '../utils/EditingContextUtils';

export interface IPropertyChangeHandler {
  (value: any, path: string[]): void | Promise<void>;
}

export type GetSubPropertiesSnapshot = (action: IShowDialogAction) => void;
export type BeforeSaveCallback = () => Promise<void> | void;

/** this is defined client side in fd.toolbox.ts in Web.Core */
export interface ActionLinkInfo {
  Name: string;
  DisplayType: ActionDisplayType;
}

// Description: null
// EditorClassName: null
// EditorType: 0
// FlowId: "4de8c235-cfc5-11eb-b6f2-349971000b84"
// FlowSessionId: "ac46404f-5cab-46cb-8dcf-11ac17929595"
// FlowStepId: "9328ef8f-adac-4567-862a-3c542a4ca88b"
// FlowStepName: "Invalid Mapping"
// ForInput: true
// IsFlow: true
// MvcEditorClassName: "$DP.Common.ShowMappingEditor"
// MvcEditorGroupName: "Primary"
// Name: "Edit Input Mappings (8)"
// ShowInPropertyGrid: true

/** this is defined client side in fd.toolbox.ts in Web.Core */
export interface ActionLinkModel {
  info: ActionLinkInfo;
  action: () => void;
}

/**
 * Quick changes are things like one-click remove actions, that this implementation
 * does not know how to do, yet. (Since it doesn't have access to server proxies, yet.)
 */
export interface IQuickChangeAction {
  /** Unique action name */
  name: string;
  path: string[];
  index?: number;
  value?: any;
}

export interface IShowDialogAction {
  node: IPropertyChild;
  path: string[];
  options?: any;
  name?: any;
  onDone?: () => void;
  editingContextId: string;
  onOpen?: () => void;
}

export interface NumberFormatOptions {
  numberDecimalSeparator: string;
  numberGroupSeparator: string;
}

export interface NumberFormatOptions {
  numberDecimalSeparator: string;
  numberGroupSeparator: string;
}

export interface EmbeddedPropertyGridProps extends IReportsChildCountProps {
  parentSearch: string;
  hideTopCategory: boolean;
}

const SEARCH_THRESHOLD = 12;

/**
 * Props to render Property Grid and respond to its changes.
 *
 * TODO limit these, now that we can make API calls with ES Module proxies. We need to be able to use this from React
 * without all the MVC props for simple use-cases. We'll probably leave all of them, but make many optional, so we
 * can respond to some changes on the MVC side.
 */
export interface IPropertyGridProps extends IAddBeforeSavePromises {
  /**
   * Providing an editingContextId makes this fetch snapshots itself.
   */
  editingContextId?: string;
  /** some designers have a sessionId, and some of their editors will depend on it */
  sessionId?: string;

  /**
   * Providing an snapshot overwrites any locally fetched snapshots.
   */
  snapshot?: ContextProviderNode; // this could be the only required one... or maybe just an editing context ID
  /**
   * pass values back to MVC to handle in one shot, like dropping a value from a list
   * @deprecated - shouldn't be necessary now we have REST helpers in this project.
   */
  onQuickChange?: QuickChangeActionHandler;
  editorLinks?: any; // EditorLink
  editorActions?: any; // EditorLink
  mappingContext?: MappingContext;
  onShowDialogue?: ShowDialogueHandler;
  /** Define if you need control buttons, otherwise can skip */
  controlButtonProps?: IPropertyGridButtonsProps;
  /** Override default behavior based on child-count */
  hideSearch?: boolean;
  // TODO use singleton module for this config instead of passing it via context.
  numberFormatOptions?: NumberFormatOptions;
  /**
   * Pass Validation issues that are found up to the parent context.
   *
   * TODO - likely won't need this, per changes to property-grid.ts#updateValidationIssues
   * which ensures they are fresh from the server before proceeding.
   */
  passValidation?: (issues: NodeValidationIssues) => void;

  /**
   * A callback that needs to be _invoked_ and `await`ed before proceeding.
   */
  addBeforeSaveCallback?: (path: string[], callback: BeforeSaveCallback) => void;

  onUnMount?: () => void;
  /**
   * if this is embedded within another editor / parent property grid.
   */
  embeddedProps?: EmbeddedPropertyGridProps;
  /**
   * Set the tab index for a particular property grid.
   * Editors will share the tabindex, and will be navigated by DOM order.
   */
  tabIndex?: number;
  renderLegacyEditor?: (options: LegacyEditorOptions) => void;
  /**
   * Some Places has their own custom validation
   */
  hideValidation?: boolean;
  /**
   * Pass this index to render only one node at a time
   */
  nodeIndex?: number;
  /**In case where we do not want to render entire pg but a particular category */
  categoryToRender?: string;

  /** `true` or `undefined` to hide a root category if there's only one; `false` to show it. */
  hideTopCategory?: boolean;

  /** true to force loading state */
  loading?: {
    isLoading: boolean;
    height: number;
  };

  /** Before refreshing PG */
  beforeSnapshotUpdate?: (snapshot: ContextProviderNode) => ContextProviderNode;

  onValueChange?: () => void;
}

const getContentClassNames = (hideTopCategory: boolean, embedded?: boolean) =>
  classNames('dpc-property-grid__editors', {
    // for embedded, parent element handles layout:
    'dpc-property-grid__editors--hidden-single-category': hideTopCategory && !embedded,
  });

/**
 * Property Grid Component, which fetches and maintains a ContextProviderNode model.
 *
 * @param props to render
 */
export function PropertyGrid(props: IPropertyGridProps) {
  const [searchString, setSearchString] = useState('');
  const [isLoading, setIsLoading] = useState(!props.snapshot);
  const editingContext = getEditingContext(props);
  const prevEditingContext = useRef<string>();
  const prevSnapshotProps = useRef<ContextProviderNode>();
  // be very careful copying to props to state. This is generally an anti-pattern except for edge cases like this:
  const [snapshot, dispatchNodeChange] = useReducer(contextProviderNodeReducer, props.snapshot);
  //17ms is roughly 60fps. Make this an app-wide setting?
  const debouncedUpdate = useMemo(() => debounce(dispatchNodeChange, 17), [dispatchNodeChange]);

  const {
    addBeforeSavePromise,
    categoryToRender,
    onUnMount,
    embeddedProps,
    hideSearch,
    nodeIndex,
    removeBeforeSavePromise,
    tabIndex = 1,
    hideValidation = false,
    beforeSnapshotUpdate,
  } = props;
  const hideTopCategory = shouldHideTopCategory(snapshot, embeddedProps?.hideTopCategory || props.hideTopCategory);
  // a property will need a path itself if top category is hidden, based on the root category node:
  const rootNodeName = snapshot?.Children?.[0]?.Node.Name || '';
  const path = useMemo(() => (hideTopCategory ? [rootNodeName] : []), [hideTopCategory, rootNodeName]);

  const [globalValidation, setGlobalValidation] = useState<ValidationIssue[]>([]);
  const editorsContainer = useRef<HTMLElement | null>(null);

  const { refresh, onChange, setSnapShot, addEditPromise, removeEditPromise } = useUpdatesPropertyEditors({
    editingContext,
    dispatchNodeChange,
    debouncedUpdate,
    addBeforeSavePromise,
    removeBeforeSavePromise,
    beforeSnapshotUpdate,
  });

  const { suspend, resume } = useSubscribeToEditingContext({
    debouncedUpdate,
    editingContext,
    getSubscriberId: () => categoryToRender || nodeIndex?.toString() || '',
    prevEditingContext,
    refresh,
    snapshot,
  });

  const onSearch = (search: string) => setSearchString(search);
  const clearSearch = () => setSearchString('');

  /**
   * Filter context based on local search string state
   */
  const filteredEditors = useMemo(() => {
    let search = embeddedProps ? embeddedProps.parentSearch : searchString;
    search = (categoryToRender ? `${categoryToRender} ${search}` : search).trim();
    const children = getChildrenFromSnapshot(snapshot, hideTopCategory);
    if (hideTopCategory && path[0].toUpperCase().includes(search.toUpperCase())) {
      // handle hidden top category, also catch edge case where search matches the hidden category.
      return children.sort(compareEditorChildren);
    }
    // filter all, and sort top level of nodes for this grid:
    return providerNodeSearch(search, children).sort(compareEditorChildren);
  }, [embeddedProps, hideTopCategory, path, searchString, snapshot, categoryToRender]);

  const validationIssues = useMemo(() => {
    const rootValidations = snapshot?.ValidationIssues || [];
    // prefer any validations attached to the root node over ones just in local state,
    // otherwise there will be duplicates passed to the parent
    const globals = rootValidations.length ? [] : globalValidation;
    return getAllValidationIssues(snapshot, globals);
  }, [globalValidation, snapshot]);

  const showSearch = hideSearch === true ? false : getContextChildrenTotal(snapshot, nodeIndex) > SEARCH_THRESHOLD;

  // if this causes render cycle, put in effect:
  embeddedProps?.hideTopCategory && embeddedProps.reportChildCount?.(filteredEditors.length);

  /**
   * to dispose the property grid
   */
  useEffect(() => {
    return () => {
      if (onUnMount) {
        onUnMount();
      }
    };
  }, [onUnMount]);

  useEffect(() => {
    // handle fetching snapshot when only ID is passed
    if (editingContext !== prevEditingContext.current) {
      // whenever there's a new editing context id:

      // clear search
      // setSearchString(''); // this is too aggressive, but we need some other

      // make sure we have up-to-date data and validations:
      if (!snapshot && !props.snapshot) {
        // snapshot prop was not provided, we need to fetch it from the editingContext;
        refresh();
        return;
      }
    }

    // fire this effect only for editingContext and snapshot changes:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContext, snapshot]);

  useEffect(() => {
    // we need to update the global validations every time snapshot is changed.
    if (snapshot && !hideValidation) {
      checkForGlobalValidationIssues(snapshot).then(setGlobalValidation);
    }
  }, [hideValidation, snapshot]);

  useEffect(() => {
    // handle updates from outside
    if (props.snapshot && props.snapshot !== prevSnapshotProps.current) {
      // a new snapshot is passed from outside, we need to use it, instead
      setSnapShot(props.snapshot);
      prevSnapshotProps.current = props.snapshot;
    }
    // fire this hook only for new snapshot object:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.snapshot]);

  // set isLoading based on snapshot, this also they care if snapshot is provided externally
  useEffect(() => {
    setIsLoading(!snapshot);
  }, [snapshot]);

  useEffect(() => {
    // TODO - likely won't need this, per changes to property-grid.ts#updateValidationIssues
    // which ensures they are fresh from the server before proceeding.
    props.passValidation && props.passValidation(validationIssues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationIssues]);

  useEffect(() => {
    // Keep track of prev/next editing context.
    // This effect MUST be AFTER any that compare prevEditingContext, or else those comparisons will always be wrong.
    prevEditingContext.current = editingContext;
  }, [editingContext]);

  const context = propertyEditorContextHelper(
    props,
    {
      ...defaultPropertyEditorContext,
      onChange: (val, path) => {
        props.onValueChange && props.onValueChange();
        return onChange(val, path);
      },
      refreshContext: () => {
        // manually refresh only if the subscription handler is missing:
        // if (subScribeToUpdateEvents) {
        //   return;
        // }
      },
      refresh: () => {
        refresh();
      },
    },
    searchString
  );

  const awaitAbleContext: IAwaitableContext = {
    addPromise: props.addBeforeSavePromise ?? addEditPromise,
    removePromise: props.removeBeforeSavePromise ?? removeEditPromise,
  };

  return (
    <PropertyEditorContextProvider
      value={context}
      tabs={{ tabIndex }}
      awaitable={awaitAbleContext}
      editorScrollContainer={editorsContainer.current}
    >
      <section className="dpc-property-grid">
        {!embeddedProps && (
          <section className="dpc-property-grid__header">
            <EditorLinks
              links={props.editorLinks}
              actions={props.editorActions}
              suspendParentUpdates={suspend}
              resumeParentUpdates={resume}
            />
            {showSearch && (
              <SearchField
                className="dpc-property-grid__search"
                onClear={clearSearch}
                onSearch={onSearch}
                value={searchString}
              />
            )}
          </section>
        )}
        <section
          className={getContentClassNames(hideTopCategory, embeddedProps?.hideTopCategory)}
          ref={editorsContainer}
        >
          <EditorChildren
            {...{
              nodes: filteredEditors,
              onChange,
              path,
              nodeIndex,
              skipSort: true,
              isLoading: isLoading,
              embedded: !!embeddedProps,
            }}
          />
        </section>
        {globalValidation.length ? (
          <section className="dpc-property-grid__global-validations">
            <ValidationError
              {...getValidationSummary(globalValidation)}
              role="list"
              className="dpc-property-grid__global-validation"
            />
          </section>
        ) : null}
        {props.controlButtonProps && (
          <section className="dpc-property-grid__footer">
            <PropertyGridButtons
              {...props.controlButtonProps}
              hasChildren={!!getChildrenFromSnapshot(snapshot).length}
              disableSave={props.controlButtonProps.disableSave}
            />
          </section>
        )}
        {(isLoading || props.loading?.isLoading) && !embeddedProps && (
          <DebounceComponent>
            <div className="dpc-property-grid__loading-mask">
              <Spinner className="dpc-property-grid__loader" />
            </div>
          </DebounceComponent>
        )}
      </section>
    </PropertyEditorContextProvider>
  );
}

async function checkForGlobalValidationIssues(node: ContextProviderNode) {
  const { EditingContextId: editingContext } = node;
  try {
    if (!node.ValidationIssues?.length) {
      // this path seems to make less sense in the newer constructs.
      // cursory investigation suggests it's just crawling the ContextProviderNode server side.
      // const DYNAMIC_CONTEXT_ID = 'DynamicContextID_';
      // if (node.EditingContextId?.indexOf(DYNAMIC_CONTEXT_ID) === 0) {
      //   return  Decisions.Web.Mvc.Host.Models.PropertyGrid.DynamicObjectContextProvider.GetValidationIssues(editingContext);
      // }
      // get them from the server:
      const all = await getInvalidReasons({ contextProviderRegistrationId: editingContext });
      return filterGlobalValidations(node, all);
    } else {
      return filterGlobalValidations(node, node.ValidationIssues);
    }
  } catch (err) {
    console.error('Property Grid Global Issues Loading Error', err);
  }
  return [];
}

function filterGlobalValidations(node: ContextProviderNode, issues: ValidationIssue[]) {
  return issues.filter(issue => {
    if (!issue.ReferenceProperty) {
      // no ref props is global.
      return true;
    } else {
      // no matching child, means it's a "group" validation, show alongside global:
      const path = issue.ReferenceProperty.split(REF_PROP_SEPARATOR);
      const { child } = findContextChildByPath(path, node);
      return child ? false : true;
    }
  });
}
